import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import { Icon, Tooltip } from 'gu-ui-kit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import emailValidator from 'utils/validators/emailValidator'
import * as Yup from 'yup'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import urlValidator from 'utils/validators/urlValidator'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/submitButtons'

export const MicrosoftAdsSchema = () =>
  Yup.object().shape({
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    network: Yup.string().required('common::validation_messages.required'),
    email: Yup.string()
      .test('emailValidator', 'common::validation_messages.incorrect_email_format', emailValidator)
      .required('common::validation_messages.required'),
    advertising_link: Yup.string()
      .test('urlValidator', 'common::validation_messages.incorrect_url_format', urlValidator)
      .required('common::validation_messages.required'),
  })

let CreateMicrosoftAdsAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  formHeader,
  networkId,
  form,
  initialValues,
  array,
  multiLogins,
}) => {
  const selector = formValueSelector(form)

  const { t } = useTranslation()
  const submitAccountData = useAccountCreateFormSubmit(form, onClose, multiLogins)

  const { email, user } = useSelector((state) => ({
    email: selector(state, 'email'),
    user: state['app'].user,
  }))

  useEffect(() => {
    user.name && email === initialValues.email && autofill('email', user.name)
  }, [user])

  return (
    <>
      {formHeader}

      <form
        onSubmit={handleSubmit(submitAccountData)}
        noValidate
      >
        <Field
          disabled
          name="network"
          type="hidden"
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={'margin-bottom-16'}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.adgram.fields.login.label'}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="email"
            type="email"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.adgram.fields.email.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.register_account.microsoft-advertising.fields.email.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'secondary-grey'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            name="advertising_link"
            type="text"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.microsoft-advertising.fields.advertising_link.label',
            )}
            t={t}
          />
        </div>

        <SubmitButtons
          submitting={submitting}
          onClose={onClose}
        />
      </form>
    </>
  )
}

CreateMicrosoftAdsAccountForm = reduxForm({
  form: 'CreateMicrosoftAdsAccountForm',
  initialValues: {
    login: '',
    network: '',
    email: '',
    advertising_link: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateMicrosoftAdsAccountForm)

CreateMicrosoftAdsAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  networkType: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  formHeader: PropTypes.node,
}

export default CreateMicrosoftAdsAccountForm
